/**
 * Client-side CSV builder + downloader.
 *
 * Mirrors the backend `Tyndo.Core.Text.Csv` guarantees byte-for-byte so an
 * export produced in the browser is escaped identically to one streamed from
 * the gateway. Two distinct concerns are handled together:
 *
 *   1. **RFC-4180 quoting.** A field is wrapped in double-quotes (embedded
 *      quotes doubled) whenever it contains a comma, a double-quote, a CR, an
 *      LF, or leading/trailing whitespace — the whitespace case because
 *      trimming-on-open silently mangles values that legitimately start/end
 *      with a space. A lone `\r` (old-Mac line ending, or a CR pasted into a
 *      comment) is treated as line-breaking, so it forces quoting too.
 *   2. **Formula-injection neutralisation** (OWASP CSV injection). A spreadsheet
 *      evaluates a cell whose first meaningful character is `=`, `+`, `-`, `@`,
 *      or a leading TAB/CR as a formula, so attacker-controlled text like
 *      `=cmd|'/c calc'!A1` or `@SUM(...)` executes on open. We prefix a single
 *      apostrophe guard, which the spreadsheet strips on display but which
 *      defuses the formula. The guard is applied BEFORE quoting and sees past a
 *      single leading double-quote (`"=cmd` is as dangerous as `=cmd`).
 *
 * Rows are separated by CRLF (RFC-4180 §2.1). This is the escaper the
 * list-archetype wires as its default CSV export handler, so no call site
 * hand-rolls (and under-escapes) its own.
 */

/** Lead characters a spreadsheet treats as the start of a formula. */
const FORMULA_LEAD_CHARS = new Set(['=', '+', '-', '@', '\t', '\r']);

/**
 * A field must be quoted when it carries a comma, a double-quote, a CR, an LF,
 * or leading/trailing whitespace. The lone-CR check matches the backend fix for
 * the old escapers that only looked at `\n`.
 */
function needsQuoting(value: string): boolean {
  if (value.length === 0) return false;
  const first = value[0]!;
  const last = value[value.length - 1]!;
  if (/\s/.test(first) || /\s/.test(last)) return true;
  for (const c of value) {
    if (c === ',' || c === '"' || c === '\r' || c === '\n') return true;
  }
  return false;
}

/**
 * Prefix an apostrophe guard when the value's first meaningful character is a
 * formula lead. Sees past a single leading double-quote so a pre-quoted value
 * (`"=cmd`) is defused like a bare `=cmd`.
 */
function guardAgainstFormulaInjection(value: string): string {
  let index = 0;
  if (value[0] === '"' && value.length > 1) index = 1;
  const lead = value[index]!;
  if (FORMULA_LEAD_CHARS.has(lead)) return "'" + value;
  return value;
}

/**
 * Escape a single field for safe, injection-resistant CSV output. `null` /
 * `undefined` are treated as empty. Non-string values are stringified first
 * (numbers/booleans render as-is; objects via `String(...)`).
 */
export function escapeCsvField(value: unknown): string {
  if (value == null) return '';
  const str = typeof value === 'string' ? value : String(value);
  if (str.length === 0) return '';
  // Decide quoting from the ORIGINAL value: the injection guard may prepend an
  // apostrophe, which would otherwise hide a leading TAB/CR that made the field
  // need quoting in the first place.
  const mustQuote = needsQuoting(str);
  const guarded = guardAgainstFormulaInjection(str);
  return mustQuote ? '"' + guarded.replace(/"/g, '""') + '"' : guarded;
}

/** Join one ordered set of raw field values into an escaped CSV line (no EOL). */
export function csvRow(fields: readonly unknown[]): string {
  return fields.map(escapeCsvField).join(',');
}

/**
 * Build a full CSV document from rows of raw field values. Rows are separated
 * by CRLF and a trailing CRLF terminates the final record (RFC-4180 §2.1).
 * Returns the empty string when there are no rows.
 */
export function toCsv(rows: ReadonlyArray<readonly unknown[]>): string {
  if (!rows || rows.length === 0) return '';
  return rows.map(csvRow).join('\r\n') + '\r\n';
}

/** A record → row mapping: each column names a header and a value accessor. */
export interface CsvColumn<Row> {
  /** Header cell text. */
  header: string;
  /** Extract the raw cell value for `row` (escaping is handled downstream). */
  value: (row: Row) => unknown;
}

export interface DownloadCsvOptions<Row> {
  /** Suggested file name. `.csv` is appended when missing. Defaults to `export.csv`. */
  filename?: string;
  /**
   * Column definitions. When provided, the first output row is the headers and
   * each data row is built by mapping every column's `value(row)`. Omit to pass
   * pre-shaped `rows` directly.
   */
  columns?: ReadonlyArray<CsvColumn<Row>>;
  /**
   * Pre-shaped rows of raw field values, used when `columns` is not supplied.
   * Ignored when `columns` is given.
   */
  rows?: ReadonlyArray<readonly unknown[]>;
  /**
   * Prepend a UTF-8 BOM so Excel opens non-ASCII (accents, CJK) correctly.
   * Defaults to `true`.
   */
  withBom?: boolean;
}

function ensureCsvExtension(name: string): string {
  return /\.csv$/i.test(name) ? name : `${name}.csv`;
}

/**
 * Build a hardened CSV from `data` and trigger a browser download.
 *
 * Two call shapes:
 *
 * ```ts
 * // 1. Column-mapped (headers + accessors):
 * downloadCsv(orders, {
 *   filename: 'work-orders',
 *   columns: [
 *     { header: 'Order', value: (o) => o.id },
 *     { header: 'Qty',   value: (o) => o.quantity },
 *   ],
 * });
 *
 * // 2. Pre-shaped rows (first row is usually your header):
 * downloadCsv(null, { filename: 'report', rows: [['A','B'], [1, 2]] });
 * ```
 *
 * Every field routes through {@link escapeCsvField}, so callers never
 * hand-escape. In a non-DOM runtime (SSR / tests) the download is a no-op and
 * the built CSV string is returned for inspection.
 */
export interface DownloadCsvTextOptions {
  /** Suggested file name. `.csv` is appended when missing. */
  filename?: string;
  /** Prepend a UTF-8 BOM so Excel opens non-ASCII correctly. Defaults to `true`. */
  withBom?: boolean;
}

/**
 * Save an ALREADY-BUILT CSV body (one the server produced, so the backend
 * `Tyndo.Core.Text.Csv` escaper has already run) as a download.
 *
 * This is the counterpart to {@link downloadCsv}: that one ASSEMBLES rows in the
 * browser and must escape them; this one must NOT re-escape an escaped body. It
 * exists so callers stop hand-rolling the Blob/anchor/objectURL dance, which in
 * practice drops the BOM, revokes the object URL before the click has resolved,
 * and omits the `document.body` append Firefox needs.
 *
 * Returns the body for callers/tests in a non-DOM runtime.
 */
export function downloadCsvText(body: string, options: DownloadCsvTextOptions = {}): string {
  const { filename = 'export.csv', withBom = true } = options;
  const payload = withBom ? '\ufeff' + body : body;

  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof Blob === 'undefined'
  ) {
    return body;
  }

  const blob = new Blob([payload], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = ensureCsvExtension(filename);
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return body;
}

export function downloadCsv<Row>(
  data: readonly Row[] | null | undefined,
  options: DownloadCsvOptions<Row> = {},
): string {
  const { filename = 'export.csv', columns, rows, withBom = true } = options;

  let matrix: ReadonlyArray<readonly unknown[]>;
  if (columns && columns.length > 0) {
    const header = columns.map((c) => c.header);
    const body = (data ?? []).map((row) => columns.map((c) => c.value(row)));
    matrix = [header, ...body];
  } else {
    matrix = rows ?? [];
  }

  const csv = toCsv(matrix);
  const payload = withBom ? '﻿' + csv : csv;

  // SSR / test / no-DOM: skip the download, return the string so callers/tests
  // can still assert on the escaped output.
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof Blob === 'undefined'
  ) {
    return csv;
  }

  const blob = new Blob([payload], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = ensureCsvExtension(filename);
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoke on the next tick so the click's navigation has resolved.
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return csv;
}
